import { useEffect, useState } from 'react'

import { ApiError, can, refusalReason } from '../../lib/api'
import { useSave } from '../../lib/save'
import { findingDeadlines, saveFindingDeadlines, type FindingDeadlines } from '../../lib/workflows'
import { ErrorBox, Loading } from '../../components/DataTable'
import { Dialog, Status } from '../../components/kit'
import { useToast } from '../../components/ui'
import '../../styles/sweep.css'

// How long a finding is given to be put right, per severity, when it is raised
// without a date. Every path that raises a finding reads these (a person, a
// score, a website check, the exit plan sweep, an unreachable vendor, the
// statutory watch), and a finding already raised keeps its date.
//
// The order rule is checked here as well as by the API so the button says why
// it is not offered, rather than letting somebody press it to find out.

const ROWS: { key: keyof Pick<FindingDeadlines, 'criticalDays' | 'highDays' | 'mediumDays' | 'lowDays'>; label: string }[] = [
  { key: 'criticalDays', label: 'Critical' },
  { key: 'highDays', label: 'High' },
  { key: 'mediumDays', label: 'Medium' },
  { key: 'lowDays', label: 'Low' },
]

type Draft = Record<(typeof ROWS)[number]['key'], string>

function toDraft(d: FindingDeadlines): Draft {
  return {
    criticalDays: String(d.criticalDays),
    highDays: String(d.highDays),
    mediumDays: String(d.mediumDays),
    lowDays: String(d.lowDays),
  }
}

function problemOf(d: Draft): string {
  const n = ROWS.map((r) => Number(d[r.key]))
  for (let i = 0; i < n.length; i++) {
    const v = n[i] as number
    if (!Number.isInteger(v) || v < 1 || v > 365) return (ROWS[i]?.label ?? '') + ' has to be a whole number of days from 1 to 365.'
  }
  for (let i = 1; i < n.length; i++) {
    if ((n[i - 1] as number) > (n[i] as number)) return 'A more severe finding cannot be given longer than a less severe one.'
  }
  return ''
}

export function RemediationDeadlines({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const toast = useToast()
  const [saved, setSaved] = useState<FindingDeadlines | null>(null)
  const [shipped, setShipped] = useState<FindingDeadlines | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [note, setNote] = useState('')
  const [error, setError] = useState<unknown>(null)
  const saving = useSave(open, 'Finding deadlines')
  const busy = saving.busy
  const [refused, setRefused] = useState('')

  useEffect(() => {
    if (!open) return
    setRefused('')
    findingDeadlines()
      .then((r) => {
        setSaved(r.deadlines)
        setShipped(r.shipped)
        setDraft(toDraft(r.deadlines))
        setNote(r.note)
        setError(null)
      })
      .catch(setError)
  }, [open])

  const mayWrite = can('config.write')
  const problem = draft ? problemOf(draft) : ''
  const dirty = draft !== null && saved !== null && JSON.stringify(draft) !== JSON.stringify(toDraft(saved))

  function save() {
    if (!draft || problem) return
    setRefused('')
    saving.send(
      saveFindingDeadlines({
        criticalDays: Number(draft.criticalDays),
        highDays: Number(draft.highDays),
        mediumDays: Number(draft.mediumDays),
        lowDays: Number(draft.lowDays),
      }),
      {
        done: (r) => {
          setSaved(r.deadlines)
          setDraft(toDraft(r.deadlines))
          toast(r.note)
          onSaved()
          onClose()
        },
        failed: (err) => setRefused(err instanceof ApiError ? (err.issues[0]?.detail ?? err.message) : String(err)),
      },
    )
  }


  return (
    <Dialog
      shape="modal"
      kind="form"
      open={open}
      onClose={onClose}
      title="Finding deadlines"
      busy={busy}
      primary={
        mayWrite
          ? {
              label: busy ? 'Saving' : 'Save deadlines',
              off: problem !== '' ? problem : !dirty && 'Nothing has been changed',
              onClick: save,
              actionId: 'tprm.settings-workflows.a01',
            }
          : undefined
      }
      note={
        mayWrite ? undefined : (
          <>
            <Status tone="neutral">Read only</Status>
            <span>{refusalReason('config.write')}</span>
          </>
        )
      }
    >
      {error != null ? (
        <ErrorBox error={error} what="The finding deadlines" />
      ) : !draft || !saved ? (
        <Loading what="the finding deadlines" />
      ) : (
        <div className="wf-deadlines">
          <p className="obs-what">{note}</p>
          <div className="field-row wf-deadline-grid">
            {ROWS.map((r) => (
              <label key={r.key}>
                {r.label}, days
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={365}
                  value={draft[r.key]}
                  disabled={!mayWrite}
                  onChange={(e) => setDraft({ ...draft, [r.key]: e.target.value })}
                />
                {shipped && <span className="field-note">Shipped default {shipped[r.key]} days</span>}
              </label>
            ))}
          </div>
          {problem && <p className="note stop">{problem}</p>}
          {refused && <p className="note stop">{refused}</p>}
          <p className="field-note">
            {saved.configured
              ? 'Set by ' + (saved.updatedBy ?? 'somebody') + (saved.updatedAt ? ' on ' + new Date(saved.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '')
              : 'These are the shipped deadlines. Nobody here has changed them.'}
          </p>
        </div>
      )}
    </Dialog>
  )
}
